/**
 * Pure transforms for the day-of-week × hour congestion heatmap ("when does
 * this PS actually peak?"). Turns the sparse {@link HeatmapCell} list (only
 * slots with history; up to 7×24) into a dense Monday-first grid plus the
 * scaling/summary helpers the view needs. Deterministic, no clock, no DOM.
 */

import type { HeatmapCell } from "@/api/generated/model";

/** Short day labels, Monday-first (index 0 = ISO day 1). */
export const DAY_SHORT_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
/** Full day labels, Monday-first. */
export const DAY_LONG_IT = [
  "Lunedì",
  "Martedì",
  "Mercoledì",
  "Giovedì",
  "Venerdì",
  "Sabato",
  "Domenica",
];

const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;

export interface HeatmapGridRow {
  /** ISO day of week (1 = Monday … 7 = Sunday). */
  dayOfWeek: number;
  dayShort: string;
  dayLong: string;
  /** 24 entries, hour 0..23; `null` where the slot has no history. */
  cells: (HeatmapCell | null)[];
}

function dayShort(dayOfWeek: number): string {
  return DAY_SHORT_IT[dayOfWeek - 1] ?? `G${dayOfWeek}`;
}

function dayLong(dayOfWeek: number): string {
  return DAY_LONG_IT[dayOfWeek - 1] ?? `Giorno ${dayOfWeek}`;
}

/**
 * Build the dense 7×24 grid (Monday-first, hour 0→23) from the sparse cell
 * list. Slots with no data are `null`. Out-of-range cells are ignored.
 */
export function buildHeatmapGrid(cells: HeatmapCell[]): HeatmapGridRow[] {
  const byKey = new Map<string, HeatmapCell>();
  for (const c of cells) {
    if (c.dayOfWeek < 1 || c.dayOfWeek > DAYS_PER_WEEK) continue;
    if (c.hourLocal < 0 || c.hourLocal > HOURS_PER_DAY - 1) continue;
    byKey.set(`${c.dayOfWeek}-${c.hourLocal}`, c);
  }

  const rows: HeatmapGridRow[] = [];
  for (let d = 1; d <= DAYS_PER_WEEK; d++) {
    const hours: (HeatmapCell | null)[] = [];
    for (let h = 0; h < HOURS_PER_DAY; h++) {
      hours.push(byKey.get(`${d}-${h}`) ?? null);
    }
    rows.push({ dayOfWeek: d, dayShort: dayShort(d), dayLong: dayLong(d), cells: hours });
  }
  return rows;
}

/** Largest average waiting across all cells (0 when empty) — the colour-scale top. */
export function maxAvgWaiting(cells: HeatmapCell[]): number {
  let max = 0;
  for (const c of cells) {
    if (c.avgWaiting > max) max = c.avgWaiting;
  }
  return max;
}

/** Mean average-waiting across all populated cells (0 when empty) — the drill's
 *  "typical for this hospital" baseline. */
export function meanAvgWaiting(cells: HeatmapCell[]): number {
  if (cells.length === 0) return 0;
  const sum = cells.reduce((acc, c) => acc + c.avgWaiting, 0);
  return sum / cells.length;
}

/**
 * A slot's standing among its own hospital's week: how it ranks against the
 * busiest slot (`peak`) and the hospital's typical slot (`mean`). Constrained to
 * the data the heatmap payload already carries — no fabricated distribution or
 * today-vs-slot comparison (that needs API work, deferred to #218).
 */
export interface CellRank {
  /** Fraction of the weekly peak, 0..1 (0 when there's no peak to rank against). */
  fractionOfPeak: number;
  /** Standing vs. this hospital's typical slot (±15% deadband around the mean). */
  vsTypical: "busier" | "quieter" | "typical";
}

/** ±15% deadband: within this of the mean reads as "typical", not busier/quieter. */
const TYPICAL_DEADBAND = 0.15;

export function cellRank(avg: number, peak: number, mean: number): CellRank {
  const fractionOfPeak = peak > 0 ? Math.min(1, Math.max(0, avg / peak)) : 0;
  let vsTypical: CellRank["vsTypical"] = "typical";
  if (mean > 0) {
    if (avg > mean * (1 + TYPICAL_DEADBAND)) vsTypical = "busier";
    else if (avg < mean * (1 - TYPICAL_DEADBAND)) vsTypical = "quieter";
  }
  return { fractionOfPeak, vsTypical };
}

/** Confidence in a slot's average, from how many observations back it. */
export type SampleConfidence = "low" | "medium" | "high";

/** ≥20 obs → high, ≥6 → medium, else low (a single reading is always low). */
export function sampleConfidence(sampleCount: number): SampleConfidence {
  if (sampleCount >= 20) return "high";
  if (sampleCount >= 6) return "medium";
  return "low";
}

/** Normalised 0..1 intensity of a value against the scale max (0 when max ≤ 0). */
export function intensity(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(1, Math.max(0, value / max));
}

export interface PeakSlot {
  dayOfWeek: number;
  dayLong: string;
  hourLocal: number;
  avgWaiting: number;
}

/**
 * The single busiest slot (highest average waiting), or `null` when there is no
 * data / nothing above zero — powers the "most crowded: Monday 10–11" summary.
 */
export function peakSlot(cells: HeatmapCell[]): PeakSlot | null {
  let best: HeatmapCell | null = null;
  for (const c of cells) {
    if (best === null || c.avgWaiting > best.avgWaiting) best = c;
  }
  if (best === null || best.avgWaiting <= 0) return null;
  return {
    dayOfWeek: best.dayOfWeek,
    dayLong: dayLong(best.dayOfWeek),
    hourLocal: best.hourLocal,
    avgWaiting: best.avgWaiting,
  };
}

/** Format an hour as a local range label, e.g. 10 → "10–11", 23 → "23–00". */
export function formatHourRange(hour: number): string {
  const next = (hour + 1) % HOURS_PER_DAY;
  const pad = (h: number) => String(h).padStart(2, "0");
  return `${pad(hour)}–${pad(next)}`;
}
